import React, { useCallback, useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import styles from './Button.module.css';
import arrow from '../icons/arrow.svg';
import {
  Plus as PlusIcon,
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  MoreVertical as MoreIcon,
  Search as SearchIcon,
  Copy as CopyIcon,
  Check as CheckIcon,
  Mail as EmailIcon,
} from 'lucide-react';
import { isFunction } from 'lodash';
import cn from 'classnames';

const defaultContent = {
  default: 'Ok',
  'arrow-up': <img src={arrow} alt="Arrow pointing up" />,
  'arrow-down': <img src={arrow} alt="Arrow pointing down" />,
  'arrow-left': <img src={arrow} alt="Arrow pointing left" />,
  'arrow-right': <img src={arrow} alt="Arrow pointing right" />,
};

export function Button({
  variant = 'default',
  busy = false,
  pulse = false,
  disabled = false,
  ...rest
}) {
  const { children, className, ...buttonProps } = rest;
  const combinedClassName = cn(styles[variant], className, {
    [styles.pulse]: pulse || false,
  });

  return (
    <button
      {...buttonProps}
      className={combinedClassName}
      disabled={busy || disabled}
    >
      {children || defaultContent[variant]}
      {busy && <span className={styles.loader} />}
    </button>
  );
}

export default Button;

export function AddButton(props) {
  return (
    <Button className={styles.addButton} variant="circle" {...props}>
      <PlusIcon />
    </Button>
  );
}

export function EditButton(props) {
  return (
    <Button variant="icon" {...props}>
      <EditIcon />
    </Button>
  );
}

export function DeleteButton(props) {
  return (
    <Button className={styles.deleteButton} variant="circle" {...props}>
      <DeleteIcon />
    </Button>
  );
}

export function MoreButton(props) {
  return (
    <Button className={styles.moreButton} aria-label="More" {...props}>
      <MoreIcon />
    </Button>
  );
}

export function SearchButton(props) {
  return (
    <Button variant="icon" {...props}>
      <SearchIcon />
    </Button>
  );
}

export class MoreActionsButton extends React.Component {
  state = {
    hidden: false,
  };

  hide = () => {
    this.setState({ hidden: true });
  };

  render = () => {
    const { actions = [] } = this.props;
    const { hidden } = this.state;
    const contentStyle = hidden ? { opacity: '0' } : null;

    return (
      <Popup
        trigger={
          <span>
            <MoreButton />
          </span>
        }
        position="left top"
        arrow={false}
        contentStyle={contentStyle}
        onClose={() => this.setState({ hidden: false })}
      >
        {(close) => (
          <ul className="popup-menu">
            {actions.map((action, index) => {
              const isEnabled =
                action.enabled === undefined ||
                (isFunction(action.enabled)
                  ? action.enabled()
                  : action.enabled);
              const isVisible =
                action.visible === undefined ||
                (isFunction(action.visible)
                  ? action.visible()
                  : action.visible);
              return (
                isVisible && (
                  <li
                    className={isEnabled ? 'enabled' : 'disabled'}
                    key={index}
                    onClick={() => {
                      if (isEnabled && action.onClick) {
                        close();
                        action.onClick();
                      }
                    }}
                  >
                    {action.render
                      ? action.render({ close, hide: this.hide })
                      : action.name}
                  </li>
                )
              );
            })}
          </ul>
        )}
      </Popup>
    );
  };
}

export function CopyButton({ value, tooltip }) {
  // Timestamp instead of a boolean so a repeat click restarts the timer.
  const [copiedAt, setCopiedAt] = useState(0);
  const onClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.navigator?.clipboard.writeText(value || '');
      setCopiedAt(Date.now());
    },
    [value],
  );

  useEffect(() => {
    if (!copiedAt) {
      return undefined;
    }
    const timer = setTimeout(() => setCopiedAt(0), 1500);
    return () => clearTimeout(timer);
  }, [copiedAt]);

  return (
    <span
      title={tooltip || 'Copy'}
      className={styles.utilButton}
      onClick={onClick}
    >
      {copiedAt ? <CheckIcon /> : <CopyIcon />}
    </span>
  );
}

export function EmailButton({ body, subject, email, tooltip }) {
  const onClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const params = new URLSearchParams({
        subject,
        body,
      })
        .toString()
        .replaceAll('+', '%20');

      document.location.href = `mailto:${email}?${params}`;
    },
    [body, subject, email],
  );

  return (
    <span
      title={tooltip || 'Email'}
      className={styles.utilButton}
      onClick={onClick}
    >
      <EmailIcon />
    </span>
  );
}
